import React, { useCallback, useState } from "react";

import CustomButton from "components/common/Button";
import InlineLoader from "./InlineLoader";
import { listBillingCustomers, searchBillingCustomers } from "services/billing";
import type { BsPaginated } from "services/billing";

import CustomerDetailModal from "./CustomerDetailModal";
import { useCursorList } from "./useCursorList";
import { CustomChip, TableComponent } from "./ui";
import FilterSelect from "./FilterSelect";
import { BsCustomer, CUSTOMER_STATUS_OPTIONS, ON_BS_SINCE_LABEL, formatDate, statusColor, titleCase } from "./types";

const columns = [
  { id: "name", label: "Name" },
  { id: "email", label: "Email" },
  {
    id: "status",
    label: "Status",
    render: (v: string) => <CustomChip label={titleCase(v)} size="small" color={statusColor(v)} />,
  },
  { id: "monolithCustomerId", label: "Monolith ID" },
  { id: "createdAt", label: ON_BS_SINCE_LABEL, render: (v: string) => formatDate(v) },
];

export default function CustomersTab() {
  const [searchInput, setSearchInput] = useState("");
  const [term, setTerm] = useState("");
  const [status, setStatus] = useState("");
  const [selected, setSelected] = useState<BsCustomer | null>(null);

  const load = useCallback(
    (cursor?: string) =>
      (term
        ? searchBillingCustomers({ q: term, status: status || undefined, cursor, limit: 25 })
        : listBillingCustomers({ status: status || undefined, cursor, limit: 25 })) as Promise<BsPaginated<BsCustomer>>,
    [term, status]
  );
  const { rows, hasMore, loading, loadingMore, error, loadMore } = useCursorList<BsCustomer>(load);

  const applySearch = () => setTerm(searchInput.trim());
  const clearSearch = () => {
    setSearchInput("");
    setTerm("");
  };

  return (
    <div className="flex flex-col gap-1">
      <div className="flex gap-05 items-end" style={{ flexWrap: "wrap" }}>
        <input
          className="border rounded-lg px-075 py-05 para-body-m-regular"
          placeholder="Search name, email or monolith ID…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") applySearch();
          }}
          style={{ minWidth: 280 }}
        />
        <CustomButton label="Search" onClick={applySearch} />
        {term && <CustomButton label="Clear" onClick={clearSearch} />}
        <FilterSelect
          name="customers-status"
          label="Status"
          options={CUSTOMER_STATUS_OPTIONS}
          value={status}
          onChange={setStatus}
        />
      </div>

      {loading ? (
        <InlineLoader />
      ) : error ? (
        <p className="para-body-m-regular color-text-body-secondary">{error}</p>
      ) : (
        <>
          <TableComponent columns={columns} data={rows} disableSort onRowClick={(row: BsCustomer) => setSelected(row)} />
          {hasMore && (
            <div className="flex justify-center mt-05">
              <CustomButton label={loadingMore ? "Loading…" : "Load more"} onClick={loadMore} disabled={loadingMore} />
            </div>
          )}
        </>
      )}
      {selected && <CustomerDetailModal customer={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
